import { getApiBase } from '~/utils/api-base'

// Data for the public legal pages (/podminky/…) from GET /api/legal/ —
// supplier identity from the backend .env, terms version and every deadline the
// texts quote (accounts/legal.py). Fetched once per app load.

export interface LegalInfo {
  version: string
  supplier: {
    name: string
    address: string
    ico: string
    vat_note: string
    registry: string
    email: string
    phone: string
    complete: boolean
  }
  hosting_provider: string
  app_url: string
  plan: {
    name: string
    price: string
    billing_period: string
    free_months: number
    topup_price: string
    topup_percent: number
  } | null
  deadlines: {
    invoice_lead_days: number
    grace_days: number
    topup_block_days: number
    purge_warn_days: number
    purge_days: number
    account_deletion_grace_days: number
    phone_retire_days: number
  }
}

export const LEGAL_LINKS = {
  terms: '/podminky/',
  privacy: '/podminky/osobni-udaje/',
  dpa: '/podminky/zpracovatelska-smlouva/',
}

export function useLegal() {
  const info = useState<LegalInfo | null>('legal-info', () => null)
  const error = useState<string>('legal-error', () => '')

  async function load() {
    if (info.value) return info.value
    try {
      const res = await fetch(`${getApiBase()}/api/legal/`)
      if (!res.ok) throw new Error(String(res.status))
      info.value = await res.json()
      error.value = ''
    } catch {
      error.value = 'Údaje o provozovateli se nepodařilo načíst. Obnovte prosím stránku.'
    }
    return info.value
  }

  // "2026-10-13" → "13. 10. 2026"
  const versionDate = computed(() => {
    const v = info.value?.version || ''
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    return m ? `${Number(m[3])}. ${Number(m[2])}. ${m[1]}` : v
  })

  return { info, error, load, versionDate }
}

// Czech plural for days: 1 den, 2–4 dny, 5+ dní.
export function dnu(n: number): string {
  if (n === 1) return '1 den'
  if (n >= 2 && n <= 4) return `${n} dny`
  return `${n} dní`
}

export function mesicu(n: number): string {
  if (n === 1) return '1 měsíc'
  if (n >= 2 && n <= 4) return `${n} měsíce`
  return `${n} měsíců`
}
